import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { AddIcon } from 'icons/materialIcons';
import LinksToggle from './LinksToggle';
import RecommendedLinks from './RecommendedLinks';
import ContactLinksList from './ContactLinksList';
import ContactLinksDetailModal from './ContactLinkDetailModal';
import AddContactLinkModal from './AddContactLinkModal';
import UpdateContactLink from './UpdateContactLink';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { createCard, updateCard, updateCardContactLinks } from 'apis/cardApi';
import { validateUUID } from 'utils/validator';
import { useCardContext } from 'contexts/CardContext';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import { uploadImageBlob, deleteImage } from 'apis/imageStorageApi';
import { updateCardProfilePictureUrl, updateCardCoverPhotoUrl, updateCardCompanyLogoUrl } from 'apis/cardApi';
import { API_BASE_URL } from 'utils/apiUtils';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';

const LinksContainer = ({ user, setSelectedItem }) => {
  const {
    contactLinks,
    setCurrentCard,
    fetchContactLinksForCard,
    updateContactLink: contextUpdateContactLink,
    deleteContactLink: contextDeleteContactLink,
    addContactLink: contextAddContactLink,
    transferTemporaryLinks,
    saveAllContactLinksToDatabase, // ✅ ADD: Import the UUID transfer function
    clearFetchedCards,
    isInSignupFlow,
    contactLinksByCard,
    currentCardId,
    debugCurrentState
  } = useContactLinkContext();

  const [contactLinksDetailModalOpen, setContactLinksDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedLinkItem] = useState('');
  const [contactLinkType, setContactLinkType] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { cardId } = useParams();
  const { authState } = useAuth();
  const { getDefaultFollowUpEmail } = useUserPreferences();
  const userId = authState?.user?.userId;
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // PRODUCTION-READY: Enhanced card creation detection
  const isCreatingCard = () => {
    if (!cardId) return false;

    // Check if it's a UUID (contains hyphens and is 32-36 chars)
    const isUUID = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    // Check if it's a MongoDB ObjectId (24 chars, hex, no hyphens)
    const isMongoId = cardId.length === 24 && /^[0-9a-fA-F]{24}$/.test(cardId) && !cardId.includes('-');

    // UUIDs indicate card creation, MongoDB IDs are existing cards
    return isUUID;
  };

  const isTemporaryCard = isCreatingCard();

  const {
    cardState,
    updateCardId,
    setCardProfilePictureUrl,
    setCardCoverPhotoUrl,
    setCardCompanyLogoUrl,
    updateCardData,
    loadCardFromServer,
    isTemporaryCard: contextIsTemporaryCard
  } = useCardContext();

  useEffect(() => {
    if (authState.user && authState.user.userId) {
      // setUserId(authState.user.userId); // This line is removed as per the edit hint
    }
  }, [authState.user]);

  // ✅ ADD THIS TEMPORARY DEBUG SECTION
  useEffect(() => {
    console.log('🔍 LINKS_CONTAINER_DEBUG:', {
      cardId: cardId?.substring(0, 12) + '...',
      cardIdLength: cardId?.length,
      isUUID: cardId && cardId.includes('-'),
      isTemporaryCard,
      contactLinksCount: contactLinks?.length || 0,
      contactLinksTypes: contactLinks?.map(l => l.type) || [],
      timestamp: new Date().toISOString()
    });
  }, [cardId, contactLinks, isTemporaryCard]);

  // Load fresh card data from server when component mounts
  useEffect(() => {
    const loadFreshCardData = async () => {
      if (cardId && !contextIsTemporaryCard(cardId)) {
        try {
          console.log('🔍 LinksContainer: Loading fresh card data from server');
          await loadCardFromServer(cardId);
        } catch (error) {
          console.error('🔍 LinksContainer: Error loading fresh card data:', error);
        }
      }
    };

    loadFreshCardData();
  }, [cardId, contextIsTemporaryCard]); // ✅ REMOVED loadCardFromServer from dependencies

  useEffect(() => {
    if (cardId) {
      console.log('🔍 LinksContainer: Setting current card:', cardId);
      console.log('🔍 LinksContainer: Card creation check:', {
        cardId,
        isTemporaryCard,
        cardIdLength: cardId.length,
        hasHyphens: cardId.includes('-')
      });
      setCurrentCard(cardId);

      // ✅ FIXED: Check for links under both current card ID and tempCardId
      const hasContactLinksForThisCard = contactLinks && contactLinks.length > 0;
      const hasTempCardLinks = contactLinksByCard['tempCardId'] && contactLinksByCard['tempCardId'].length > 0;

      console.log('🔍 LinksContainer: Link availability check:', {
        currentCardId: cardId?.substring(0, 12) + '...',
        hasContactLinksForThisCard,
        hasTempCardLinks,
        tempCardLinksCount: contactLinksByCard['tempCardId']?.length || 0,
        currentCardLinksCount: contactLinks?.length || 0
      });

      if (!hasContactLinksForThisCard && !hasTempCardLinks) {
        console.log('🔍 LinksContainer: No contact links found for card, fetching...');
        fetchContactLinksForCard(cardId);
      } else {
        console.log('🔍 LinksContainer: Contact links available:', {
          currentCard: contactLinks?.length || 0,
          tempCard: contactLinksByCard['tempCardId']?.length || 0
        });
      }
    }
  }, [cardId, contactLinks, setCurrentCard, fetchContactLinksForCard]);

  // Sync context values when they change
  useEffect(() => {
    if (cardId && cardState[cardId]) {
      console.log('🔍 LinksContainer: Context values updated:', {
        cardTheme: cardState[cardId]?.cardTheme,
        cardLinkColor: cardState[cardId]?.cardLinkColor,
        cardName: cardState[cardId]?.cardName
      });
    }
  }, [cardId, cardState]);

  // Card data from context
  const profilePictureBlob = cardState[cardId]?.cardProfilePictureImage;
  const cardFullName = cardState[cardId]?.cardFullName || '';
  const cardJobTitle = cardState[cardId]?.cardJobTitle || '';
  const cardCompany = cardState[cardId]?.cardCompany || '';
  const cardLocation = cardState[cardId]?.cardLocation || '';
  const cardBio = cardState[cardId]?.cardBio || '';
  const cardLayout = cardState[cardId]?.contextCardLayout || 'left-aligned';
  const cardTheme = cardState[cardId]?.cardTheme || '';
  const cardLinkColor = cardState[cardId]?.cardLinkColor || '';
  const cardProfilePictureUrl = cardState[cardId]?.cardProfilePictureUrl;
  const cardCoverPhotoUrl = cardState[cardId]?.cardCoverPhotoUrl;
  const cardCompanyLogoUrl = cardState[cardId]?.cardCompanyLogoUrl;
  const cardName = cardState[cardId]?.cardName || '';

  const handleToggleVisibility = async (linkId, isVisible) => {
    console.log('Toggling visibility for link ID:', linkId, 'to:', isVisible);
    try {
      await contextUpdateContactLink(linkId, cardId, { isVisible }, true);
      console.log('Visibility updated successfully');
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const handleDelete = async (linkId) => {
    console.log('Deleting link ID:', linkId);
    try {
      await contextDeleteContactLink(linkId, cardId, userId, true);
      console.log('Contact link deleted successfully');
      setEditingLink(null);
    } catch (error) {
      console.error('Error deleting contact link:', error);
    }
  };

  const handleEditLink = (link) => {
    console.log('Editing link:', link);
    setEditingLink(link);
  };

  const handleUpdateLink = async (linkId, updatedData) => {
    try {
      console.log('🔍 LinksContainer: Starting contact link update');
      console.log('🔍 LinksContainer: linkId:', linkId);
      console.log('🔍 LinksContainer: cardId:', cardId);
      console.log('🔍 LinksContainer: updatedData:', updatedData);
      console.log('🔍 LinksContainer: current contactLinks:', contactLinks);

      await contextUpdateContactLink(linkId, cardId, updatedData, true);
      console.log('🔍 LinksContainer: Contact link updated successfully');
      setEditingLink(null);
      setShouldRefresh(true);
    } catch (error) {
      console.error('🔍 LinksContainer: Error updating contact link:', error);
      console.error('🔍 LinksContainer: Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
    }
  };

  const handleContactLinkButtonClick = (item) => {
    setContactLinkType(item);
    setSelectedLinkItem(item);
    setContactLinksDetailModalOpen(true);
  };

  // ✅ DEBUG FUNCTION - FOR DEVELOPMENT TROUBLESHOOTING
  // This function provides comprehensive debugging of contact link state and context.
  // It logs detailed information about card IDs, contact links, and context state.
  // Used during development to diagnose contact link issues.
  // The debug button that calls this function is commented out above.
  const debugContactLinks = () => {
    console.log('🔍 =================DEBUG CONTACT LINKS=================');
    console.log('🔍 PROVIDED_CARD_ID:', cardId?.substring(0, 12) + '...');
    console.log('🔍 CURRENT_CARD_ID:', currentCardId?.substring(0, 12) + '...');
    console.log('🔍 CARD_ID_MATCH:', cardId === currentCardId);
    console.log('🔍 ALL_CARD_IDS:', Object.keys(contactLinksByCard).map(id => id.substring(0, 12) + '...'));
    console.log('🔍 LINKS_FOR_PROVIDED_CARD:', contactLinksByCard[cardId]?.length || 0);
    console.log('🔍 LINKS_FOR_CURRENT_CARD:', contactLinksByCard[currentCardId]?.length || 0);
    console.log('🔍 CONTEXT_CONTACT_LINKS:', contactLinks?.length || 0);
    console.log('🔍 =======================================================');

    if (debugCurrentState) {
      debugCurrentState();
    }
  };

  // ✅ CONSOLE DEBUG INFO - AUTOMATIC LOGGING IN DEVELOPMENT
  // This useEffect automatically logs key state information to console during development.
  // It helps track card ID changes, temporary card status, and contact link counts.
  // Only runs in development mode and logs when cardId, isTemporaryCard, or contactLinks change.
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 LinksContainer Debug Info:', {
        cardId: cardId?.substring(0, 12) + '...',
        cardIdLength: cardId?.length,
        isUUID: cardId && cardId.includes('-'),
        isTemporary: isTemporaryCard,
        contactLinksCount: contactLinks?.length || 0,
        contactLinksTypes: contactLinks?.map(l => l.type).join(', ') || 'None'
      });
    }
  }, [cardId, isTemporaryCard, contactLinks]);

  const handleImagesUpload = async (newCardId) => {
    try {
      if (cardState[cardId]?.cardProfilePictureUrl) {
        const blob = await fetch(cardState[cardId].cardProfilePictureUrl).then(r => r.blob());
        const profilePicUrl = await uploadImageBlob(
          blob,
          setCardProfilePictureUrl,
          'card',
          'profile',
          newCardId
        );
        await updateCardProfilePictureUrl(newCardId, profilePicUrl);
      }

      if (cardState[cardId]?.cardCoverPhotoUrl) {
        const blob = await fetch(cardState[cardId].cardCoverPhotoUrl).then(r => r.blob());
        const coverPhotoUrl = await uploadImageBlob(
          blob,
          setCardCoverPhotoUrl,
          'card',
          'cover',
          newCardId
        );
        await updateCardCoverPhotoUrl(newCardId, coverPhotoUrl);
      }

      if (cardState[cardId]?.cardCompanyLogoUrl) {
        const blob = await fetch(cardState[cardId].cardCompanyLogoUrl).then(r => r.blob());
        const companyLogoUrl = await uploadImageBlob(
          blob,
          setCardCompanyLogoUrl,
          'card',
          'logo',
          newCardId
        );
        await updateCardCompanyLogoUrl(newCardId, companyLogoUrl);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  };

  // PRODUCTION-READY: Enhanced save card function
  const handleSaveCard = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoading(true);

    try {
      if (!cardFullName) {
        alert('Card name is required. Please go back to the About tab and enter at least the card name.');
        return;
      }

      updateCardData(cardId, {
        cardFullName,
        cardJobTitle,
        cardCompany,
        cardLocation,
        cardBio,
        contextCardLayout: cardLayout,
        cardTheme,
        cardLinkColor
      });

      const cardDetails = {
        fullName: cardFullName,
        jobTitle: cardJobTitle || '',
        company: cardCompany || '',
        location: cardLocation || '',
        bio: cardBio || '',
        cardLayout: cardLayout || 'left-aligned',
        cardTheme: cardTheme || '',
        cardName: cardName || 'My Card',
        linkColor: cardLinkColor || '',
        userId: userId,
        isActive: true,
        is_default: false,
        autoFollowUpIntroEmail: getDefaultFollowUpEmail(),
      };

      console.log('🚀 CARD_CREATION: Creating permanent card with data:', cardDetails);
      console.log('🚀 CARD_CREATION: Context values read:', {
        cardTheme,
        cardLinkColor,
        cardName,
        cardState: cardState[cardId]
      });
      console.log('🚀 CARD_CREATION: Current UUID card ID:', cardId);

      // Step 1: Create permanent card
      const response = await createCard(cardDetails);
      console.log('✅ CARD_CREATED: Permanent card created:', response);

      if (!response || !response.card || !response.card._id) {
        throw new Error('Failed to create card - invalid response from server');
      }

      const permanentCardId = response.card._id;
      console.log('✅ PERMANENT_CARD_ID:', permanentCardId);

      // Step 2: Upload images
      await handleImagesUpload(permanentCardId);

      // ✅ FIXED: Step 3 - UUID Contact Links Transfer
      try {
        console.log('🔄 TRANSFER_START: Starting UUID contact links transfer...');
        console.log('🔄 TRANSFER_PARAMS:', {
          fromCardId: cardId?.substring(0, 12) + '...',
          toCardId: permanentCardId?.substring(0, 8) + '...',
          userId: userId?.substring(0, 10) + '...',
          currentContactLinksCount: contactLinks?.length || 0
        });

        // ✅ FIXED: Check for links under both current card ID and tempCardId
        const currentContactLinks = contactLinks || [];
        const tempCardLinks = contactLinksByCard['tempCardId'] || [];
        const allLinksToTransfer = [...currentContactLinks, ...tempCardLinks];

        console.log('🔍 TRANSFER_LINK_CHECK:', {
          currentContactLinks: currentContactLinks.length,
          tempCardLinks: tempCardLinks.length,
          allLinksToTransfer: allLinksToTransfer.length,
          currentCardId: cardId?.substring(0, 12) + '...',
          tempCardId: 'tempCardId'
        });

        if (allLinksToTransfer.length === 0) {
          console.log('ℹ️ NO_LINKS_TO_TRANSFER: Skipping transfer');
        } else {
          console.log('🔄 TRANSFERRING_LINKS: Found links to transfer:', allLinksToTransfer.length);

          // ✅ CRITICAL FIX: Use the correct transfer function for UUID cards
          // Pass null as sourceCardId to let the function auto-detect the source
          const transferResponse = await saveAllContactLinksToDatabase(
            permanentCardId,  // Permanent card ID (destination)
            userId,           // User ID
            null             // Let function auto-detect source (tempCardId or UUID)
          );

          console.log('🔄 TRANSFER_RESPONSE:', transferResponse);

          if (transferResponse && transferResponse.success) {
            console.log('✅ TRANSFER_SUCCESS:', {
              transferred: transferResponse.saved || transferResponse.transferred,
              total: transferResponse.total,
              links: transferResponse.links?.length
            });

            // Verify transfer worked
            try {
              await new Promise(resolve => setTimeout(resolve, 1500)); // Longer delay for UUID transfer

              const verificationResponse = await axios.get(`${API_BASE_URL}/api/contact-link/card/${permanentCardId}`);
              console.log('🔍 TRANSFER_VERIFICATION:', verificationResponse.data);

              if (verificationResponse.data.success && verificationResponse.data.contactLinks.length > 0) {
                console.log('✅ VERIFICATION_PASSED: Transfer successful');
              } else {
                console.warn('⚠️ VERIFICATION_FAILED: No permanent links found');
              }
            } catch (verifyError) {
              console.warn('⚠️ VERIFICATION_ERROR:', verifyError);
            }

            // Clear cache and refresh
            clearFetchedCards();
            await fetchContactLinksForCard(permanentCardId);

          } else {
            console.error('❌ TRANSFER_FAILED:', transferResponse);
            throw new Error('Transfer failed with unsuccessful response');
          }
        }

      } catch (transferError) {
        console.error('❌ TRANSFER_ERROR:', transferError);
        console.error('❌ TRANSFER_ERROR_DETAILS:', {
          message: transferError.message,
          response: transferError.response?.data,
          status: transferError.response?.status
        });

        // ✅ IMPROVED: Better error handling - still continue but warn user
        console.warn('⚠️ TRANSFER_FAILED_CONTINUING: Transfer failed but card creation will continue');

        // Don't fail the entire card creation, but log the issue
        // The user will have a card but may need to recreate contact links
      }

      // Step 4: Update card context
      updateCardId(permanentCardId);

      // Step 5: Navigate with proper timing
      console.log('✅ CARD_CREATION_COMPLETE: Process completed successfully');

      // Delay navigation to ensure all processes complete
      setTimeout(() => {
        navigate('/dashboard/mycards');
      }, 2000); // ✅ Increased delay for UUID transfer

    } catch (error) {
      console.error('❌ CARD_CREATION_FAILED:', error);
      alert('Failed to create card. Please try again.');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const handleNavigateToAbout = () => {
    updateCardData(cardId, {
      cardFullName,
      cardJobTitle,
      cardCompany,
      cardLocation,
      cardBio,
      contextCardLayout: cardLayout,
      cardTheme,
      cardLinkColor
    });

    if (setSelectedItem) {
      setSelectedItem('about');
    }

    navigate(`/dashboard/mycards/add-card/${cardId}?section=about`);
  };

  if (editingLink) {
    return (
      <Box display="flex" alignItems="start" flexDirection="column" gap={2}>
        <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginTop: 2 }}>
          Edit Link
        </Typography>
        <UpdateContactLink
          link={editingLink}
          onCancel={() => setEditingLink(null)}
          onUpdate={handleUpdateLink}
          onDelete={handleDelete}
        />
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="start" flexDirection="column" gap={2}>
      <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginTop: 2 }}>
        Links
      </Typography>

      {/* ✅ DEBUG BUTTON - COMMENTED OUT FOR PRODUCTION
          This button was used during development to manually trigger detailed debugging
          of contact link state and context. It logs comprehensive debug info to console.
          Uncomment if you need to debug contact link issues in development.
      */}
      {/* 
      {process.env.NODE_ENV === 'development' && (
        <Button
          onClick={debugContactLinks}
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            backgroundColor: '#ffeb3b',
            color: '#000',
            '&:hover': { backgroundColor: '#fff176' }
          }}
        >
          🔍 DEBUG CONTACT LINKS
        </Button>
      )}
      */}

      <LinksToggle onAddLinksAndContact={() => setOpenModal(true)} />

      {/* ✅ DEBUG INFO MOVED TO CONSOLE - HIDDEN UI ELEMENT
          This was previously a visible debug box in the UI.
          Debug information is now logged to console instead for cleaner UI.
          The hidden Box element is kept for potential future use.
      */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ display: 'none' }}>
          {/* Hidden debug info - logged to console instead */}
        </Box>
      )}

      {/* ✅ FIXED: Smart link combination - only combine for new card creation */}
      {(() => {
        let linksToDisplay;

        if (isTemporaryCard) {
          // ✅ NEW CARD CREATION: Combine links from both sources
          const currentCardLinks = contactLinks || [];
          const tempCardLinks = contactLinksByCard['tempCardId'] || [];
          linksToDisplay = [...currentCardLinks, ...tempCardLinks];

          console.log('🔍 ContactLinksList: NEW CARD - Combined links:', {
            currentCardLinks: currentCardLinks.length,
            tempCardLinks: tempCardLinks.length,
            allLinks: linksToDisplay.length,
            linkTypes: linksToDisplay.map(l => l.type)
          });
        } else {
          // ✅ EXISTING CARD: Use only database links
          linksToDisplay = contactLinks || [];

          console.log('🔍 ContactLinksList: EXISTING CARD - Database links:', {
            linksCount: linksToDisplay.length,
            linkTypes: linksToDisplay.map(l => l.type)
          });
        }

        return (
          <ContactLinksList
            contactLinks={linksToDisplay}
            onToggleVisibility={handleToggleVisibility}
            onDelete={handleDelete}
            onEdit={handleEditLink}
            disableEditDelete={false}
          />
        );
      })()}

      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mt={4}>
        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: "#4f4f4f" }}>
          Recommended Links
        </Typography>

        <Button
          onClick={() => setOpenModal(true)}
          sx={{
            color: '#2aaff7',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'none',
            backgroundColor: 'transparent',
            borderRadius: '20px',
            padding: '8px 16px',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#d9f1ff' },
          }}
          endIcon={<AddIcon sx={{ transform: 'scaleX(-1)' }} />}
        >
          Explore all Links
        </Button>
      </Box>

      <RecommendedLinks onContactLinkButtonClick={handleContactLinkButtonClick} />

      {/* Navigation and Save Buttons */}
      {isTemporaryCard && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleNavigateToAbout}
            sx={{
              padding: '10px 30px',
              color: '#000000',
              fontWeight: "bold",
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: '#000000',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#000000',
              },
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={handleSaveCard}
            sx={{
              backgroundColor: '#000000',
              padding: '10px 30px',
              color: '#ffffff',
              fontWeight: "bold",
              textTransform: 'none',
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Card'}
          </Button>
        </Box>
      )}

      <ContactLinksDetailModal
        open={contactLinksDetailModalOpen}
        handleClose={() => {
          setContactLinksDetailModalOpen(false);
          setShouldRefresh(true);
        }}
        userId={userId}
        cardId={cardId}
        contactLinkType={contactLinkType}
        isTemporaryCard={isTemporaryCard}
        existingContactLinks={contactLinks}
        isInSignupFlow={isInSignupFlow}
      />

      <AddContactLinkModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
          setShouldRefresh(true);
        }}
        userId={userId}
        cardId={cardId}
        isTemporaryCard={isTemporaryCard}
        existingContactLinks={contactLinks}
        isInSignupFlow={isInSignupFlow}
      />
    </Box>
  );
};

export default LinksContainer;